import * as React from "react";
import { RefObject, useState } from "react";
import CatalogViewer from "./commons/components/CatalogViewer";
import EmailTemplate from "./commons/model/EmailTemplate";
import ModalPopup from "./commons/components/modal/ModalPopup";
import EmailTemplateViewer from "./EmailTemplateViewer";
import ReportTypeUtils from "./reports/ReportTypeUtils";
import FlowType from "./commons/model/FlowType";
import { AvailableEntitiesSettings } from "./commons/model/EntitySettings";
import { createRoot } from "react-dom/client";

interface CatalogEmailTemplateViewerProps {
  entities: AvailableEntitiesSettings;
  templates: EmailTemplate[];
  createUrl?: string;
}

enum EmailTemplatesCategories {
  SMILE_SIMULATION = "SMILE_SIMULATION",
  SMILE_OR_FULL_ASSESSMENT = "SMILE_OR_FULL_ASSESSMENT",
  BOOKING = "BOOKING",
  CLINICAL_SCORING = "CLINICAL_SCORING",
}

export default function CatalogEmailTemplateViewer({
  entities,
  templates,
  createUrl,
}: CatalogEmailTemplateViewerProps) {
  const [templateToPreview, setTemplateToPreview] = useState<EmailTemplate>();
  const iframeRef: RefObject<HTMLIFrameElement> = React.createRef<HTMLIFrameElement>();
  const emailTemplatesManual = templates.filter((template) => !template.auto);
  const emailTemplatesAuto = templates.filter((template) => template.auto);

  const itemsByType = [
    {
      type: i18n["Page.Catalog.EmailTemplates.Category.Practice"],
      items: emailTemplatesManual,
      createUrl: createUrl,
      itemsByCategory: [
        {
          items: emailTemplatesManual,
          title: i18n["Page.Catalog.EmailTemplates.Category.General"],
          color: "#7B8794",
        },
      ],
    },
    {
      type: i18n["Page.Catalog.EmailTemplates.Category.Automatic"],
      items: emailTemplatesAuto,
      itemsByCategory: [
        {
          items: emailTemplatesAuto.filter(
            (template) => template.category === EmailTemplatesCategories.SMILE_SIMULATION
          ),
          title: i18n["Page.Catalog.EmailTemplates.Category.SmileSimulation"],
          color: ReportTypeUtils.getReportTypeColor(FlowType.SMILE_SIMULATION),
        },
        {
          items: emailTemplatesAuto.filter(
            (template) => template.category === EmailTemplatesCategories.SMILE_OR_FULL_ASSESSMENT
          ),
          title: i18n["Page.Catalog.EmailTemplates.Category.SmileOrFullAssessment"],
          color: ReportTypeUtils.getReportTypeColor(FlowType.FULL_ASSESSMENT),
        },
        {
          items: emailTemplatesAuto.filter(
            (template) => template.category === EmailTemplatesCategories.BOOKING
          ),
          title: i18n["Page.Catalog.EmailTemplates.Category.Booking"],
          color: "#DF4336",
        },
        {
          items: emailTemplatesAuto.filter(
            (template) => template.category === EmailTemplatesCategories.CLINICAL_SCORING
          ),
          title: i18n["Page.Catalog.EmailTemplates.Category.ClinicalScoring"],
          color: "#3153d9",
        },
      ],
    },
  ];

  const updateTemplateToPreview = (emailTemplate: EmailTemplate) => {
    setTemplateToPreview(emailTemplate);
  };

  const viewer = (template: EmailTemplate) => {
    return (
      <EmailTemplateViewer
        url={template.templateUrl}
        preview={() => updateTemplateToPreview(template)}
      />
    );
  };

  const confirmOnExternalLinkClick = (e) => {
    if (
      confirm(
        i18n["Page.Catalog.EmailTemplates.Security.ExternalLinkAlert"].replaceAll(
          "{}",
          iframeRef.current.contentWindow.location.hostname
        )
      )
    ) {
      return; //redirect
    } else {
      e.preventDefault();
      return false; //don't redirect
    }
  };

  const onPreviewReady = () => {
    iframeRef.current.style.height =
      iframeRef.current.contentWindow.document.body.scrollHeight + 1 + "px";

    Array.from(iframeRef.current.contentWindow.document.querySelectorAll("a"))
      .filter((l) => l.hostname !== iframeRef.current.contentWindow.location.hostname)
      .forEach((el) => el.addEventListener("click", confirmOnExternalLinkClick));
  };

  return (
    <>
      <CatalogViewer<EmailTemplate>
        itemsByType={itemsByType}
        itemRenderer={viewer}
        labels={{
          title: i18n["Page.Catalog.EmailTemplates.Title"],
          searchPlaceholder: i18n["Page.Catalog.EmailTemplates.Search"],
          createButton: i18n["Page.Catalog.EmailTemplates.Create"],
        }}
        isEmailCatalogTemplate={true}
        entities={entities}
      />
      {templateToPreview && (
        <ModalPopup closeModal={() => setTemplateToPreview(null)} class="email-previewer">
          <div className="subject">{templateToPreview.subject}</div>
          <iframe
            ref={iframeRef}
            src={document.location.origin + templateToPreview.previewUrl}
            onLoad={onPreviewReady}
          />
        </ModalPopup>
      )}
    </>
  );
}

CatalogEmailTemplateViewer.loadComponent = (
  elementId: string,
  props: CatalogEmailTemplateViewerProps
) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<CatalogEmailTemplateViewer {...props} />);
};
